import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import memoize from 'lodash/memoize';

import {
    ERROR_REQUIRED,
    ERROR_INVALID_FORMAT,
    ERROR_UPLOADING_INVALID_TYPE,
    DEFAULT_UPLOADING_INVALID_TYPE,
    ERROR_DUPLICATE_EMAIL,
    DEFAULT_DUPLICATE_EMAIL,
    getDefaultRequiredMessage,
    getDefaultInvalidResponseFormatMessage,
} from 'common/entities/form/errorMessages';
import connectFormErrorMessageStyles from '../../hoc/connectFormErrorMessagesStyles';

import parseFontSize from '../../utils/parseFontSize';

import './FormErrorMessage.styl';

import {
    ERROR_UPLOADING,
    STYLING_DEFAULT_ERROR_MESSAGE,
    ERROR_TYPE_CHECKOUT_V2_REQUIRED,
} from '../../constants/errorTypes';

const ErrorMessage = styled.p`
    font-family: ${(props) => props.font};
    font-size: ${(props) => parseFontSize(props.fontSize)};
    color: ${(props) => props.color};
    white-space: pre-wrap;
`;

const ERROR_LABEL_WORD_LIMIT = 10;

/**
 * @param {string} label
 * @return {string}
 */
const shortenLabel = memoize((label) => {
    const words = (label || '').split(' ');
    return words.length > ERROR_LABEL_WORD_LIMIT
        ? `${words.slice(0, ERROR_LABEL_WORD_LIMIT - 4).join(' ')} ... ${words.slice(words.length - 3).join(' ')}`
        : label;
});

const replaceBrackets = (text, replacements) =>
    (text.match(/\[[^\]]*\]/g) || []).reduce((acc, next) => {
        const item = next.replace(/[[\]']+/g, '');
        const replacement = replacements[item];

        return replacement ? acc.replace(next, replacement) : acc;
    }, text);

// Here's the translation from error type to UI friendly message.
export const messages = {
    [ERROR_REQUIRED]: (label, customMessage) =>
        customMessage ? replaceBrackets(customMessage, { label }) : getDefaultRequiredMessage(label),
    [ERROR_INVALID_FORMAT]: (label, customMessage) =>
        customMessage ? replaceBrackets(customMessage, { label }) : getDefaultInvalidResponseFormatMessage(label),
    [ERROR_UPLOADING_INVALID_TYPE]: (label, customMessage) =>
        customMessage ? replaceBrackets(customMessage, { label }) : DEFAULT_UPLOADING_INVALID_TYPE.value,
    [ERROR_DUPLICATE_EMAIL]: () => DEFAULT_DUPLICATE_EMAIL.value,
    [ERROR_UPLOADING]: () => 'There has been an error uploading the file.',
    [STYLING_DEFAULT_ERROR_MESSAGE]: () => 'This is a sample error message.',
    [ERROR_TYPE_CHECKOUT_V2_REQUIRED]: () => 'Required.',
    default: () => 'Error',
};

const BASE_COMPONENT_NAME = 'FormErrorMessage';

const FormErrorMessage = (props) => (
    <ErrorMessage
        className={`${BASE_COMPONENT_NAME} splash-copy`}
        font={props.font && props.font.value}
        fontSize={props.fontSize}
        color={props.color && props.color.value}
        role="alert"
    >
        {messages[props.errorType] !== undefined
            ? messages[props.errorType](shortenLabel(props.label), props.formErrorMessages[props.errorType])
            : props.errorType}
    </ErrorMessage>
);

FormErrorMessage.propTypes = {
    errorType: PropTypes.string.isRequired,

    /** form node label, if this error is referencing a specific node */
    label: PropTypes.string,

    // Form connectFormErrorMessageStyles.
    font: PropTypes.object,
    fontSize: PropTypes.number,
    color: PropTypes.object,
    formErrorMessages: PropTypes.object,
};

FormErrorMessage.defaultProps = {
    font: undefined,
    fontSize: undefined,
    color: undefined,
    label: undefined,
    formErrorMessages: undefined,
};

export default connectFormErrorMessageStyles(FormErrorMessage);
